import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Mail, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";

const SERVICE_LINKS = [
  { to: "/services/web-development", label: "Website Development" },
  { to: "/services/app-software", label: "Mobile App Development" },
  { to: "/services/app-software", label: "Custom Software" },
  { to: "/services", label: "UI/UX Design" },
  { to: "/services/it-cloud-security", label: "Cloud Solutions" },
  { to: "/services/it-cloud-security", label: "Cybersecurity" },
  { to: "/services", label: "IT Consulting" },
  { to: "/services/it-cloud-security", label: "IT Support & Maintenance" },
];

export default function Footer() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  return (
    <footer className="border-t border-white/5 bg-[#030712]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-electric to-cyan-400">
                <span className="font-display text-lg font-black text-ink">T</span>
              </span>
              <span className="font-display text-lg font-bold text-white">
                Tech<span className="text-cyan-400">Axis</span> <span className="text-[10px] uppercase tracking-widest text-slate-500">Ltd.</span>
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              UK technology partner turning business ideas into reliable, secure and scalable digital products.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-3 py-1.5" data-testid="footer-status-pill">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-wider text-cyan-300">
                {status ? `${status.message} · ${status.uptime}` : "NOC · All Systems Operational"}
              </span>
            </div>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Services</h3>
            <ul className="mt-4 space-y-2.5">
              {SERVICE_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid={`footer-service-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Company</h3>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/about" className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid="footer-about-link">About Us</Link></li>
              <li><Link to="/services" className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid="footer-services-link">Services</Link></li>
              <li><Link to="/products" className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid="footer-products-link">Products</Link></li>
              <li><Link to="/quote" className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid="footer-quote-link">Get a Quote</Link></li>
              <li><Link to="/contact" className="text-sm text-slate-400 transition-colors hover:text-cyan-300" data-testid="footer-contact-link">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Contact</h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                <span data-testid="footer-address">861b Stockport Road,<br />Manchester M19 3PW, UK</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                <a href="mailto:hello@techaxisltd.co.uk" className="transition-colors hover:text-cyan-300" data-testid="footer-email-link">hello@techaxisltd.co.uk</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                <a href="tel:+447358002510" className="transition-colors hover:text-cyan-300" data-testid="footer-phone-link">+44 7358 002510</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                <a href="mailto:support@techaxisltd.co.uk" className="transition-colors hover:text-cyan-300" data-testid="footer-support-email-link">support@techaxisltd.co.uk</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-8 sm:flex-row">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} TechAxis Ltd. Registered in England &amp; Wales. All rights reserved.</p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="back-to-top-button"
            className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
          >
            Back to top <ArrowUp className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </footer>
  );
}
